// Pure particle data for the 2D platformer scene: small square bursts spawned
// from `PlatformerEvent`s (a hit, a landing, a pickup) and stepped forward
// with velocity, gravity and a lifetime. No three.js, no DOM, no React, so
// the update is deterministic and unit-testable; `Platformer2DScene.tsx`'s
// `Particles2D` component owns the rendering.
//
// Coordinates match `platformer2d.ts`: tile units, x right, y DOWN (the
// renderer negates y). A positive `gravity` therefore pulls a particle toward
// larger y, which is the floor.
import type { PlatformerEvent } from './platformer2d';

/** One live particle. Positions and sizes are in tiles, velocities in tiles
 * per second, lifetimes in seconds. */
export interface Particle2D {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Seconds of life remaining; the particle is dropped once this reaches 0. */
  life: number;
  /** The lifetime it started with, so a renderer can fade or shrink by
   * `life / maxLife`. */
  maxLife: number;
  /** Edge length in tiles. */
  size: number;
  /** Any CSS colour string. */
  colour: string;
  /** Tiles per second squared of downward acceleration. */
  gravity: number;
  /** Fraction of horizontal velocity kept per second (1 = none lost). */
  drag: number;
}

/** The event kinds that spawn a burst. */
export type ParticleBurstKind = 'hit' | 'enemy-death' | 'hurt' | 'land' | 'dash' | 'pickup' | 'victory';

/** Hard cap on live particles so a long fight cannot grow the list without
 * bound; the oldest particles are dropped first when a burst overflows it. */
export const MAX_PARTICLES_2D = 256;

/** Per-kind burst shape. Counts stay within the 6 to 16 range the design
 * asks for; colours are the fallbacks used when an event carries none. */
interface BurstPreset {
  count: number;
  colour: string;
  /** Speed range in tiles per second. */
  minSpeed: number;
  maxSpeed: number;
  /** Lifetime range in seconds. */
  minLife: number;
  maxLife: number;
  /** Size range in tiles. */
  minSize: number;
  maxSize: number;
  gravity: number;
  drag: number;
  /** Emission cone: centre angle in radians (0 = right, negative = up since
   * y points down) and half-width in radians. A half-width of PI is a full
   * circle. */
  angle: number;
  spread: number;
}

const BURST_PRESETS: Record<ParticleBurstKind, BurstPreset> = {
  hit: {
    count: 10,
    colour: '#fde68a',
    minSpeed: 4,
    maxSpeed: 9,
    minLife: 0.18,
    maxLife: 0.36,
    minSize: 0.08,
    maxSize: 0.16,
    gravity: 14,
    drag: 0.15,
    angle: 0,
    spread: Math.PI,
  },
  'enemy-death': {
    count: 16,
    colour: '#f97316',
    minSpeed: 3,
    maxSpeed: 8,
    minLife: 0.4,
    maxLife: 0.8,
    minSize: 0.1,
    maxSize: 0.22,
    gravity: 18,
    drag: 0.2,
    angle: -Math.PI / 2,
    spread: Math.PI,
  },
  hurt: {
    count: 8,
    colour: '#f87171',
    minSpeed: 3,
    maxSpeed: 7,
    minLife: 0.25,
    maxLife: 0.45,
    minSize: 0.08,
    maxSize: 0.14,
    gravity: 16,
    drag: 0.2,
    angle: -Math.PI / 2,
    spread: Math.PI,
  },
  land: {
    count: 6,
    colour: '#a8a29e',
    minSpeed: 1.2,
    maxSpeed: 3,
    minLife: 0.2,
    maxLife: 0.4,
    minSize: 0.08,
    maxSize: 0.14,
    gravity: 4,
    drag: 0.05,
    // Dust kicks out sideways and slightly up along the floor.
    angle: -Math.PI / 2,
    spread: Math.PI * 0.55,
  },
  dash: {
    count: 8,
    colour: '#38bdf8',
    minSpeed: 1,
    maxSpeed: 3,
    minLife: 0.2,
    maxLife: 0.35,
    minSize: 0.06,
    maxSize: 0.12,
    gravity: 0,
    drag: 0.3,
    angle: 0,
    spread: Math.PI,
  },
  pickup: {
    count: 12,
    colour: '#4ade80',
    minSpeed: 2,
    maxSpeed: 5,
    minLife: 0.35,
    maxLife: 0.7,
    minSize: 0.08,
    maxSize: 0.14,
    gravity: -3,
    drag: 0.4,
    angle: -Math.PI / 2,
    spread: Math.PI,
  },
  victory: {
    count: 16,
    colour: '#facc15',
    minSpeed: 4,
    maxSpeed: 10,
    minLife: 0.6,
    maxLife: 1.2,
    minSize: 0.1,
    maxSize: 0.2,
    gravity: 12,
    drag: 0.2,
    angle: -Math.PI / 2,
    spread: Math.PI * 0.6,
  },
};

/** Mulberry32: the same tiny deterministic PRNG `fx/particles.ts` uses, kept
 * local so this module stays free of every other runtime import. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface ParticleBurstOptions {
  /** Overrides the preset's colour (a hit burst uses the weapon's colour). */
  colour?: string;
  /** Overrides the preset's count; clamped to 1..64. */
  count?: number;
  /** Direction bias: a dash to the left throws its trail to the right. */
  facing?: 'left' | 'right';
}

/**
 * Spawn one burst of `kind` at (`x`, `y`). Pure: the same `seed` always
 * yields the same particles, so a replay or a test can reproduce a burst
 * exactly. The scene passes an incrementing per-event index as the seed.
 */
export function spawnParticleBurst(
  kind: ParticleBurstKind,
  x: number,
  y: number,
  seed: number,
  options: ParticleBurstOptions = {},
): Particle2D[] {
  const preset = BURST_PRESETS[kind];
  const rand = mulberry32(seed * 7919 + kind.length);
  const count = Math.max(1, Math.min(64, options.count ?? preset.count));
  const colour = options.colour ?? preset.colour;
  const out: Particle2D[] = [];
  let angle = preset.angle;
  if (kind === 'dash' && options.facing) {
    // The trail streams out behind the dash.
    angle = options.facing === 'right' ? Math.PI : 0;
  } else if (kind === 'hit' && options.facing) {
    angle = options.facing === 'right' ? 0 : Math.PI;
  }
  const spread = kind === 'dash' || kind === 'hit' ? (options.facing ? Math.PI * 0.4 : preset.spread) : preset.spread;
  for (let i = 0; i < count; i++) {
    const theta = angle + (rand() * 2 - 1) * spread;
    const speed = preset.minSpeed + rand() * (preset.maxSpeed - preset.minSpeed);
    const life = preset.minLife + rand() * (preset.maxLife - preset.minLife);
    const size = preset.minSize + rand() * (preset.maxSize - preset.minSize);
    out.push({
      x: x + (rand() - 0.5) * 0.3,
      y: y + (rand() - 0.5) * 0.3,
      vx: Math.cos(theta) * speed,
      vy: Math.sin(theta) * speed,
      life,
      maxLife: life,
      size,
      colour,
      gravity: preset.gravity,
      drag: preset.drag,
    });
  }
  return out;
}

/**
 * Advance every particle by `dt` seconds and drop the ones whose life ran
 * out. Returns a new array; the input is never mutated, matching the
 * "return the next state" shape `stepPlatformer` uses. Semi-implicit Euler:
 * gravity updates velocity first, then position, so a particle at rest
 * under gravity starts moving on the very first step.
 */
export function stepParticles(particles: readonly Particle2D[], dt: number): Particle2D[] {
  if (dt <= 0) return particles.slice();
  const out: Particle2D[] = [];
  for (const p of particles) {
    const life = p.life - dt;
    if (life <= 0) continue;
    const dragKeep = p.drag > 0 ? Math.pow(1 - Math.min(0.999, p.drag), dt) : 1;
    const vx = p.vx * dragKeep;
    const vy = p.vy + p.gravity * dt;
    out.push({
      ...p,
      x: p.x + vx * dt,
      y: p.y + vy * dt,
      vx,
      vy,
      life,
    });
  }
  return out;
}

/** Append `burst` to `particles`, dropping the oldest entries if the result
 * would exceed {@link MAX_PARTICLES_2D}. Returns a new array. */
export function appendParticles(particles: readonly Particle2D[], burst: readonly Particle2D[]): Particle2D[] {
  const merged = particles.concat(burst);
  if (merged.length <= MAX_PARTICLES_2D) return merged;
  return merged.slice(merged.length - MAX_PARTICLES_2D);
}

export interface EventParticleOptions {
  /** Colour for `hit` bursts; the equipped weapon's colour. */
  hitColour?: string;
  /** The player's position, used for events that carry none (`land`). */
  playerX?: number;
  playerY?: number;
  playerFacing?: 'left' | 'right';
}

/**
 * The burst a `PlatformerEvent` produces, or `null` when the event has no
 * visual. `seed` should be unique per event (an incrementing counter) so two
 * bursts on the same frame do not overlap pixel for pixel.
 */
export function particlesForEvent(
  event: PlatformerEvent,
  seed: number,
  options: EventParticleOptions = {},
): Particle2D[] | null {
  switch (event.type) {
    case 'hit':
      return spawnParticleBurst('hit', event.x, event.y, seed, { colour: options.hitColour, facing: options.playerFacing });
    case 'enemy-death':
      return spawnParticleBurst('enemy-death', event.x, event.y, seed);
    case 'hurt':
      return spawnParticleBurst('hurt', event.x, event.y, seed);
    case 'land':
      if (options.playerX === undefined || options.playerY === undefined) return null;
      // Dust rises from the player's feet, not their centre.
      return spawnParticleBurst('land', options.playerX, options.playerY + 0.4, seed);
    case 'dash':
      if (options.playerX === undefined || options.playerY === undefined) return null;
      return spawnParticleBurst('dash', options.playerX, options.playerY, seed, { facing: event.direction });
    case 'pickup':
      return spawnParticleBurst('pickup', event.x, event.y, seed);
    case 'victory':
      return spawnParticleBurst('victory', event.x, event.y, seed);
    default:
      return null;
  }
}
